import { useState } from 'react';
import { PILLARS, calculateNextIndex } from '../utils/scoring';

export default function WeightedPriorities({ audit, onUpdate }) {
  const [expanded, setExpanded] = useState(false);

  const customWeights = audit.customWeights || null;
  const scores = audit.scores || {};

  // Default weights from PILLARS
  const defaultWeights = {};
  PILLARS.forEach(p => { defaultWeights[p.id] = p.weight; });

  const weights = customWeights || defaultWeights;
  const totalWeight = Object.values(weights).reduce((s, v) => s + v, 0);

  const updateWeight = (pillarId, value) => {
    const updated = { ...weights, [pillarId]: value };
    onUpdate({ ...audit, customWeights: updated });
  };

  const resetToDefault = () => {
    onUpdate({ ...audit, customWeights: null });
  };

  // Calculate custom-weighted index
  const customIndex = (() => {
    let weightedSum = 0;
    let totalW = 0;
    PILLARS.forEach(p => {
      const score = scores[p.id]?.score || 0;
      const w = weights[p.id] || 0;
      if (score > 0) {
        weightedSum += score * w;
        totalW += w;
      }
    });
    return totalW > 0 ? weightedSum / totalW : 0;
  })();

  const standardIndex = calculateNextIndex(scores);
  const delta = customIndex - standardIndex;

  return (
    <div className="card overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-3 hover:bg-neutral-800/30 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-sm">⚖️</span>
          <span className="text-xs text-neutral-400">Custom Weighting</span>
          {customWeights && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-gold">Custom</span>}
        </div>
        <span className="text-neutral-600 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-neutral-800 pt-3">
          <p className="text-xs text-neutral-500 mb-4">Adjust pillar weights to reflect what matters most to this client. Total should equal 100%.</p>

          {/* Weight sliders */}
          <div className="space-y-2 mb-4">
            {PILLARS.map(p => {
              const w = weights[p.id] || 0;
              const pct = Math.round(w * 100);
              const defaultPct = Math.round((defaultWeights[p.id] || 0) * 100);
              const isChanged = customWeights && pct !== defaultPct;

              return (
                <div key={p.id} className={`flex items-center gap-3 ${isChanged ? 'bg-brand-gold/5 rounded px-2 py-1' : ''}`}>
                  <span className="text-xs text-neutral-400 w-36 truncate">{p.name}</span>
                  <input
                    type="range"
                    min="0" max="25" step="1"
                    value={pct}
                    onChange={e => updateWeight(p.id, parseInt(e.target.value) / 100)}
                    className="tick-slider flex-1"
                    style={{ '--pct': `${(pct / 25) * 100}%`, '--color': '#C9A668', height: '6px' }}
                  />
                  <span className={`text-xs font-bold w-8 text-right ${isChanged ? 'text-brand-gold' : 'text-neutral-500'}`}>{pct}%</span>
                </div>
              );
            })}
          </div>

          {/* Total weight warning */}
          <div className={`text-xs mb-4 ${Math.round(totalWeight * 100) === 100 ? 'text-emerald-400' : 'text-red-400'}`}>
            Total: {Math.round(totalWeight * 100)}% {Math.round(totalWeight * 100) !== 100 && '(should be 100%)'}
          </div>

          {/* Comparison */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-neutral-800/40 rounded-lg p-3 text-center">
              <p className="text-xs text-neutral-500 mb-1">Standard</p>
              <p className="text-xl font-black text-neutral-400">{standardIndex.toFixed(1)}</p>
            </div>
            <div className="bg-neutral-800/40 rounded-lg p-3 text-center">
              <p className="text-xs text-neutral-500 mb-1">Custom Weighted</p>
              <p className="text-xl font-black text-brand-gold">{customIndex.toFixed(1)}</p>
            </div>
            <div className="bg-neutral-800/40 rounded-lg p-3 text-center">
              <p className="text-xs text-neutral-500 mb-1">Difference</p>
              <p className={`text-xl font-black ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-neutral-400'}`}>
                {delta > 0 ? '+' : ''}{delta.toFixed(1)}
              </p>
            </div>
          </div>

          {customWeights && (
            <button onClick={resetToDefault} className="btn-ghost text-xs w-full">Reset to Default Weights</button>
          )}
        </div>
      )}
    </div>
  );
}
